import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Toast } from '../index'
import { Cell } from '@highlight/ui'
import {
  type ReminderApiItem,
  getReminderCreationMethod,
  mapApiItemToReminderItem,
  REMINDERS_QUERY_KEY,
} from '@renderer/overlay/Sidepanel/hooks/use-reminders'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { toastManager } from '../toast-store'
import { getSourceLabel } from '@renderer/overlay/Sidepanel/components/reminders/utils/get-source-label'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

/** Subscribe to the reminders query cache reactively (not a one-time snapshot). */
function useLiveReminders(): Array<ReminderApiItem> {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<Array<ReminderApiItem>>(
    () => queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY]) ?? [],
  )

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] !== REMINDERS_QUERY_KEY) return
      if (event.type === 'updated') {
        setItems((event.query.state.data as Array<ReminderApiItem>) ?? [])
      }
    })
    setItems(queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY]) ?? [])
    return unsubscribe
  }, [queryClient])

  return items
}

interface ReminderToastProps {
  reminders: Array<ReminderApiItem>
  toastId: string
}

function ReminderToastCell({
  reminder,
  onDismiss,
}: {
  reminder: ReminderApiItem
  onDismiss: (reminderId: string) => void
}) {
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const liveReminders = useLiveReminders()

  const liveReminder = liveReminders.find((r) => r.id === reminder.id) ?? reminder
  const item = mapApiItemToReminderItem(liveReminder)

  const handleClick = () => {
    const initialTab = item.resolved ? 'resolved' : 'active'
    openViewInSidepanel('reminders', { initialTab, focusReminderId: reminder.id })
    onDismiss(reminder.id)
    AmplitudeRenderer.trackEvent('Reminder', {
      action: 'view',
      reminderId: reminder.id,
      source: 'toast',
    })
  }

  return (
    <Cell clickable onClick={handleClick} className="p-0 opacity-100 shadow-none after:hidden">
      <Cell.Header showChevron={false}>
        <Cell.Title className="text-foreground line-clamp-3 text-sm">{item.title}</Cell.Title>
      </Cell.Header>

      {item.source && (
        <Cell.Footer className="text-xs">
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            From <span className="text-foreground-secondary">{getSourceLabel(item.source)}</span>
          </span>
        </Cell.Footer>
      )}
    </Cell>
  )
}

const MAX_VISIBLE_REMINDERS = 3

export function ReminderToast({ reminders, toastId }: ReminderToastProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const liveReminders = useLiveReminders()
  const liveIds = useMemo(() => new Set(liveReminders.map((r) => r.id)), [liveReminders])
  const hasLoadedRef = useRef(false)
  if (liveReminders.length > 0) hasLoadedRef.current = true

  const remainingReminders = useMemo(
    () =>
      hasLoadedRef.current
        ? reminders.filter((r) => !dismissedIds.has(r.id) && liveIds.has(r.id))
        : reminders.filter((r) => !dismissedIds.has(r.id)),
    [reminders, dismissedIds, liveIds],
  )

  useEffect(() => {
    if (remainingReminders.length === 0 && hasLoadedRef.current) {
      toastManager.dismiss(toastId)
    }
  }, [remainingReminders.length, toastId])

  const visibleReminders = remainingReminders.slice(0, MAX_VISIBLE_REMINDERS)
  const overflowCount = remainingReminders.length - visibleReminders.length

  const handleDismissReminder = useCallback(
    (reminderId: string) => {
      const dismissed = reminders.find((r) => r.id === reminderId)
      if (dismissed) {
        AmplitudeRenderer.trackEvent('Reminder', {
          action: 'toast dismissed',
          reminderId: dismissed.id,
          type: dismissed.type,
          creationMethod: getReminderCreationMethod(dismissed),
          sourceType: dismissed.source?.type,
        })
      }

      setDismissedIds((prev) => new Set(prev).add(reminderId))

      const remaining = reminders.filter((r) => r.id !== reminderId && !dismissedIds.has(r.id))
      if (remaining.length === 0) {
        toastManager.dismiss(toastId)
      }
    },
    [reminders, dismissedIds, toastId],
  )

  if (remainingReminders.length === 0) return null

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <div className="flex w-full flex-col gap-4">
          {visibleReminders.map((reminder, i) => (
            <div
              key={reminder.id}
              className="animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300"
              style={{ animationDelay: `${i * 75}ms` }}
            >
              {i > 0 && <div className="bg-element-hover -mt-1 mb-3 h-px w-full" />}
              <ReminderToastCell reminder={reminder} onDismiss={handleDismissReminder} />
            </div>
          ))}
          {overflowCount > 0 && (
            <div className="text-foreground-placeholder text-center text-xs">
              +{overflowCount} more reminder{overflowCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </Toast.Content>
    </Toast>
  )
}
